// SPDX-License-Identifier: GPL-3.0-or-later
// Hats of the pinball SigmaBot, built from three.js primitives.
// Own code. The upstream 3D bot takes hats from a binary parts file (a GLB). The table has no
// such file, so each hat is a few primitives. The rule of the place is the same as upstream: the
// hat origin is 3 units (times the scale `k`) under the top of the body, and `k` is `hatW / 26`.
// Rules: no clock, no random. A hat is a Group in local hat units (up is +y, the front is +z).

import {
  BoxGeometry,
  ConeGeometry,
  CylinderGeometry,
  Group,
  SphereGeometry,
  TorusGeometry,
} from "three";
import type { BodyDef } from "./bodies.ts";
import { BROWN, type Colors, CREAM, GOLD, INK, PINK, STEEL, TAU, WOOD } from "./kit.ts";
import { DEPTH_K, halfAt } from "./loft.ts";
import { part } from "./parts.ts";
import type { SigmaBotBody, SigmaBotHat } from "./spec.ts";

/** What a hat builder reads. */
interface HatCtx {
  readonly kind: SigmaBotBody;
  readonly c: Colors;
  /** Scale of the hat: `hatW / 26`. */
  readonly k: number;
  /** Height of the top of the body (up is positive). */
  readonly topUp: number;
}

type HatBuilder = (g: Group, h: HatCtx) => void;

/** An upper half sphere. The open side is down and stays inside the body. */
function dome(g: Group, r: number, color: Parameters<typeof part>[2], sy = 0.8, y = 0): void {
  part(g, new SphereGeometry(r, 14, 8, 0, TAU, 0, Math.PI / 2), color, 0, y, 0, { sy });
}

function disc(g: Group, r: number, h: number, color: Parameters<typeof part>[2], y: number): void {
  part(g, new CylinderGeometry(r, r, h, 16), color, 0, y, 0);
}

const HATS: Record<Exclude<SigmaBotHat, "none">, HatBuilder> = {
  cap: (g, { c }) => {
    dome(g, 13, c.accent);
    part(g, new BoxGeometry(22, 1.6, 12), c.accentDark, 0, 1, 10);
  },
  crown: (g) => {
    part(g, new CylinderGeometry(12, 13, 8, 10), GOLD, 0, 4, 0);
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * TAU;
      part(g, new ConeGeometry(2.4, 7, 6), GOLD, Math.cos(a) * 10.5, 11.5, Math.sin(a) * 10.5);
    }
  },
  party: (g, { c }) => {
    part(g, new ConeGeometry(10, 24, 12), c.accent, 0, 12, 0);
    part(g, new SphereGeometry(2.4, 8, 6), GOLD, 0, 25, 0);
  },
  wizard: (g, { c }) => {
    part(g, new ConeGeometry(12, 30, 12), c.accentDark, 0, 15, 0);
    disc(g, 18, 1.4, c.accentDark, 0.5);
    part(g, new SphereGeometry(2, 8, 6), GOLD, 0, 31, 0);
  },
  beanie: (g, { c }) => {
    dome(g, 13.5, c.accent, 0.9);
    disc(g, 13.6, 4, c.light, 1);
    part(g, new SphereGeometry(3, 8, 6), c.light, 0, 12, 0);
  },
  tophat: (g, { c }) => {
    part(g, new CylinderGeometry(9, 9, 18, 12), INK, 0, 9, 0);
    disc(g, 15, 1.6, INK, 0.8);
    part(g, new CylinderGeometry(9.2, 9.2, 3, 12), c.accent, 0, 3, 0);
  },
  halo: (g) => {
    part(g, new TorusGeometry(10, 1.4, 6, 20), GOLD, 0, 15, 0, { rx: Math.PI / 2 });
  },
  headband: (g, { kind, c, k, topUp }) => {
    // The band is a ring at the real width of the body, 8 units under the top.
    const radius = (halfAt(kind, topUp - 8 * k) + 0.8) / k;
    part(g, new TorusGeometry(radius, 1.6, 6, 24), c.accent, 0, -5, 0, {
      rx: Math.PI / 2,
      sy: DEPTH_K[kind],
    });
  },
  bow: (g, { c }) => {
    for (const s of [-1, 1]) {
      part(g, new ConeGeometry(5, 10, 8), c.accent, s * 6, 3, 0, { rz: -s * (Math.PI / 2) });
    }
    part(g, new SphereGeometry(2.6, 8, 6), c.accentLight, 0, 3, 0);
  },
  pirate: (g) => {
    dome(g, 11, INK, 0.9);
    part(g, new CylinderGeometry(16, 16, 2, 3), INK, 0, 1, 0, { ry: Math.PI / 6 });
    part(g, new SphereGeometry(2, 8, 6), CREAM, 0, 6, 10, { sz: 0.4 });
  },
  chef: (g) => {
    part(g, new CylinderGeometry(9.5, 10, 7, 12), CREAM, 0, 3.5, 0);
    part(g, new SphereGeometry(11, 12, 8), CREAM, 0, 12, 0, { sy: 0.8 });
  },
  cowboy: (g) => {
    dome(g, 9, WOOD, 1.1);
    disc(g, 20, 1.4, WOOD, 0.7);
    part(g, new CylinderGeometry(9.2, 9.2, 2.4, 12), BROWN, 0, 2, 0);
  },
  flower: (g) => {
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * TAU;
      part(g, new SphereGeometry(3.2, 8, 6), PINK, Math.cos(a) * 4.2, 3, Math.sin(a) * 4.2);
    }
    part(g, new SphereGeometry(2.4, 8, 6), GOLD, 0, 3.4, 0);
  },
  antlers: (g) => {
    for (const s of [-1, 1]) {
      part(g, new CylinderGeometry(0.9, 1.2, 14, 6), BROWN, s * 7, 9, 0, { rz: s * -0.4 });
      part(g, new CylinderGeometry(0.7, 0.9, 7, 6), BROWN, s * 10.5, 12, 0, { rz: s * -0.9 });
    }
  },
  helmet: (g, { c }) => {
    dome(g, 14, STEEL, 0.95);
    part(g, new BoxGeometry(2.4, 2, 22), c.accent, 0, 12, 0);
  },
};

/** Add the hat to a group that sits on the top of the body. `none` adds nothing. */
export function addHat(
  parent: Group,
  kind: SigmaBotBody,
  b: BodyDef,
  c: Colors,
  hat: SigmaBotHat,
): void {
  if (hat === "none") return;
  const k = b.hatW / 26;
  const topUp = -b.topY;
  const g = new Group();
  g.name = "SigmaBotHat";
  g.position.set(0, topUp - 3 * k, 0);
  g.scale.setScalar(k);
  HATS[hat](g, { kind, c, k, topUp });
  parent.add(g);
}
